/**
 * Table cell truncate with tooltip (2-line clamp, show full content on overflow).
 * Use in Ant Design table column render for consistent UX.
 */

import React, { type ReactNode } from 'react';
import { AppTooltip } from '@/components/atoms';

const DEFAULT_EMPTY = <span className="text-gray-400">-</span>;

export interface TableCellTruncateOptions {
  /** Shown when text is empty (default: gray "-") */
  emptyPlaceholder?: ReactNode;
  /** When set, cell is clickable (keyboard Enter + click) */
  onClick?: () => void;
  /** Extra class for the inner div (e.g. cursor-pointer text-blue-700 hover:underline) */
  className?: string;
  /** Width for the cell (e.g. '200px', 200, 'w-64') */
  width?: string | number;
  /** Whether to render as HTML (dangerouslySetInnerHTML) */
  useHtml?: boolean;
  /** Number of lines to clamp (default: 2) */
  lineClamp?: number;
}

/**
 * Renders table cell content with 2-line clamp and tooltip on overflow.
 * Use in column render: render: (text) => renderTableCellTruncate(text)
 * or with options: render: (_, record) => renderTableCellTruncate(record.name, { onClick: () => ... })
 */
export function renderTableCellTruncate(
  text: string | null | undefined,
  options?: TableCellTruncateOptions
): ReactNode {
  const {
    emptyPlaceholder = DEFAULT_EMPTY,
    onClick,
    className = '',
    width,
    useHtml = false,
    lineClamp = 2,
  } = options ?? {};
  const content = text ?? '';
  const isEmpty = content === '';

  if (isEmpty) return emptyPlaceholder;

  // Handle width if provided
  const style: React.CSSProperties = {};
  let widthClass = '';
  if (width) {
    if (typeof width === 'string' && width.startsWith('w-')) {
      widthClass = width;
    } else {
      style.width = typeof width === 'number' ? `${width}px` : width;
    }
  }

  // Handle line clamp
  const clampClass = lineClamp > 0 ? `line-clamp-${lineClamp}` : '';

  // break-all ensures every character is a break point, preventing browsers from
  // widening the column to fit spaced text (e.g. U+3000 ideographic spaces) in ≤2 lines.
  const baseClass = `${clampClass} break-all min-w-0 overflow-hidden ${widthClass}`.trim();
  const combinedClass = className ? `${baseClass} ${className}` : baseClass;

  // Tooltip title - strip HTML tags if content is HTML for cleaner tooltip
  const tooltipTitle = useHtml && typeof content === 'string'
    ? content.replace(/<[^>]*>/g, '')
    : content;

  // Needs whitespace-pre-wrap when content has newlines or runs of whitespace
  // (ASCII spaces or U+3000 ideographic spaces) so they are preserved as content.
  const needsPreWrap =
    typeof content === 'string' &&
    (content.includes('\n') || / {2}|　/.test(content));

  const renderContent = () => {
    if (useHtml && typeof content === 'string') {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }
    if (needsPreWrap) {
      return <span className="whitespace-pre-wrap">{content}</span>;
    }
    return content;
  };

  const inner = onClick ? (
    <div
      role="button"
      tabIndex={0}
      className={combinedClass}
      style={style}
      onClick={onClick}
    >
      {renderContent()}
    </div>
  ) : (
    <div className={combinedClass} style={style}>{renderContent()}</div>
  );

  return (
    <AppTooltip title={tooltipTitle} showOnlyOnOverflow>
      {inner}
    </AppTooltip>
  );
}
